import { Check } from "@phosphor-icons/react";
import * as CheckboxPrimitive from "@radix-ui/react-checkbox";
import { useId, type ReactNode } from "react";

import { focusRing } from "../../foundations/a11y";
import { cn } from "../../lib/cn";

export type CheckboxProps = {
  checked?: boolean | "indeterminate";
  defaultChecked?: boolean;
  onCheckedChange?: (checked: boolean) => void;
  disabled?: boolean;
  label?: ReactNode;
  id?: string;
  className?: string;
};

export function Checkbox({
  checked,
  defaultChecked,
  onCheckedChange,
  disabled,
  label,
  id,
  className,
}: CheckboxProps) {
  const autoId = useId();
  const checkboxId = id ?? autoId;

  const box = (
    <CheckboxPrimitive.Root
      id={checkboxId}
      checked={checked}
      defaultChecked={defaultChecked}
      onCheckedChange={(value) => onCheckedChange?.(value === true)}
      disabled={disabled}
      className={cn(
        "border-border bg-surface flex size-5 shrink-0 items-center justify-center rounded-[6px] border",
        "data-[state=checked]:border-accent data-[state=checked]:bg-accent",
        "disabled:pointer-events-none disabled:opacity-50",
        focusRing,
        className,
      )}
    >
      <CheckboxPrimitive.Indicator className="text-on-accent flex">
        <Check size={14} weight="bold" aria-hidden="true" />
      </CheckboxPrimitive.Indicator>
    </CheckboxPrimitive.Root>
  );

  if (!label) return box;
  return (
    <label
      htmlFor={checkboxId}
      className="text-body text-text inline-flex cursor-pointer items-center gap-2.5"
    >
      {box}
      {label}
    </label>
  );
}
